import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Leaf, CircleDot, Activity, ShieldPlus, Eye, Compass } from 'lucide-react';
const chapters = [{
  id: 'intro',
  title: 'Introducción',
  subtitle: 'El Origen del Descubrimiento',
  icon: Compass,
  content: ['Breve biografía del Dr. Hamer', 'Contexto de la Nueva Medicina Germánica', 'Objetivo del libro: comprender las cinco leyes biológicas y su impacto en la salud']
}, {
  id: 'cap1',
  title: 'Capítulo 1: La Primera Ley Biológica',
  subtitle: 'La Ley de Hierro del Cáncer',
  icon: CircleDot,
  content: ['La Ley de Hierro del Cáncer', 'El shock biológico: qué es y cómo identificarlo', 'Relación entre psique, cerebro y órgano', 'Ejemplos prácticos de conflictos y su manifestación']
}, {
  id: 'cap2',
  title: 'Capítulo 2: La Segunda Ley Biológica',
  subtitle: 'La Ley de las Dos Fases',
  icon: Activity,
  content: ['La bifase de las enfermedades: fase activa y fase de curación', 'Cómo el cuerpo reacciona ante la resolución del conflicto', 'Ejemplos de síntomas en cada fase']
}, {
  id: 'cap3',
  title: 'Capítulo 3: La Tercera Ley Biológica',
  subtitle: 'El Sistema Ontogenético',
  icon: Leaf,
  content: ['Sistema ontogenético de los programas biológicos', 'Cómo los distintos tejidos del cuerpo responden a diferentes conflictos', 'Ejemplos concretos: tejidos endodérmicos, mesodérmicos y ectodérmicos']
}, {
  id: 'cap4',
  title: 'Capítulo 4: La Cuarta Ley Biológica',
  subtitle: 'El Sistema Ontogenético de los Microbios',
  icon: ShieldPlus,
  content: ['El rol de los microorganismos en la fase de curación', 'Bacterias, virus y hongos: aliados del proceso biológico', 'Cómo el cuerpo utiliza estos microorganismos para reparar tejidos']
}, {
  id: 'cap5',
  title: 'Capítulo 5: La Quinta Ley Biológica',
  subtitle: 'La Quintaesencia',
  icon: Eye,
  content: ['El sentido biológico de cada enfermedad', 'Por qué el síntoma tiene una función de adaptación y supervivencia', 'Ejemplos de interpretación de síntomas comunes']
}, {
  id: 'conclusion',
  title: 'Conclusión',
  subtitle: 'La Integración',
  icon: SparklesIcon,
  content: ['Integrando las cinco leyes en la vida diaria', 'Cómo transformar el dolor en conciencia', 'Reflexiones finales sobre el crecimiento personal y la salud consciente']
}];
function SparklesIcon(props: any) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-47">
      <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" data-id="element-48" />
      <path d="M5 3v4" data-id="element-49" />
      <path d="M19 17v4" data-id="element-50" />
      <path d="M3 5h4" data-id="element-51" />
      <path d="M17 19h4" data-id="element-52" />
    </svg>;
}
export function ChaptersSection() {
  const [expandedId, setExpandedId] = useState<string | null>('cap1');
  return <section className="py-24 bg-forest-dark relative" data-id="element-53">
      {/* Texture overlay */}
      <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-gold to-transparent mix-blend-overlay pointer-events-none" data-id="element-54" />

      <div className="container mx-auto px-6 max-w-4xl relative z-10" data-id="element-55">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-56">
          <h2 className="font-display text-4xl md:text-5xl text-parchment-light font-bold mb-4" data-id="element-57">
            Estructura del Libro
          </h2>
          <p className="text-gold-light text-lg max-w-2xl mx-auto" data-id="element-58">
            Un recorrido estructurado para comprender la sabiduría de tu cuerpo
          </p>
        </motion.div>

        <div className="space-y-4" data-id="element-59">
          {chapters.map((chapter, index) => {
          const isExpanded = expandedId === chapter.id;
          const Icon = chapter.icon;
          return <motion.div key={chapter.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className={`rounded-xl overflow-hidden border transition-colors duration-300 ${isExpanded ? 'bg-forest-light border-gold/40 shadow-lg shadow-black/20' : 'bg-forest-dark border-forest-light hover:border-gold/20'}`} data-id="element-60">
                <button onClick={() => setExpandedId(isExpanded ? null : chapter.id)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-61">
                  <div className="flex items-center gap-4" data-id="element-62">
                    <div className={`p-2 rounded-lg ${isExpanded ? 'bg-gold/20 text-gold' : 'bg-forest-light text-white/70'}`} data-id="element-63">
                      <Icon className="w-6 h-6" data-id="element-64" />
                    </div>
                    <div data-id="element-65">
                      <h3 className={`font-display text-xl md:text-2xl font-semibold ${isExpanded ? 'text-gold-light' : 'text-parchment-light'}`} data-id="element-66">
                        {chapter.title}
                      </h3>
                      <p className={`text-sm mt-1 ${isExpanded ? 'text-white' : 'text-white/70'}`} data-id="element-67">
                        {chapter.subtitle}
                      </p>
                    </div>
                  </div>
                  <motion.div animate={{
                rotate: isExpanded ? 180 : 0
              }} transition={{
                duration: 0.3
              }} className={`shrink-0 ml-4 ${isExpanded ? 'text-gold' : 'text-white/70'}`} data-id="element-68">
                    <ChevronDown className="w-6 h-6" data-id="element-69" />
                  </motion.div>
                </button>

                <AnimatePresence data-id="element-70">
                  {isExpanded && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} data-id="element-71">
                      <div className="px-6 pb-6 pt-2 ml-14" data-id="element-72">
                        <ul className="space-y-3" data-id="element-73">
                          {chapter.content.map((item, idx) => <motion.li key={idx} initial={{
                      opacity: 0,
                      x: -10
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} transition={{
                      delay: 0.1 + idx * 0.05
                    }} className="flex items-start gap-3 text-white" data-id="element-74">
                              <span className="text-gold mt-1.5 text-xs" data-id="element-75">
                                ✦
                              </span>
                              <span className="leading-relaxed" data-id="element-76">{item}</span>
                            </motion.li>)}
                        </ul>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}